<template>
  <div v-if="loading" class="text-center mt-5 font-2xl text-muted">
    <i class="fal fa-spinner fa-spin" /> Laboratory Data Loading...
  </div>
  <div v-else class="animated fadeIn">
    <basic-heading :title="laboratory.name" class="mb-3">
      <template #right-content>
        <b-button-toolbar key-nav aria-label="Key Lab Actions">
          <header-button-group :button_config="button_config">
            <template #post_buttons>
              <button
                v-show="checkPermission('delete laboratory')"
                title="Delete laboratory"
                class="btn btn-danger ml-1"
                @click="showDeleteModal"
              >
                <i class="fal fa-trash" />
              </button>
            </template>
          </header-button-group>
        </b-button-toolbar>
      </template>
    </basic-heading>
    <permission-alert-gate :permissions="'read laboratory'">
      <template #gated-content>
        <b-row>
          <b-col class="pr-0">
            <laboratory-card :laboratory="laboratory" />
          </b-col>
          <b-col>
            <laboratory-contact-card :laboratory="laboratory" />
          </b-col>
        </b-row>
        <laboratory-detail-cardstack :laboratory="laboratory" />
      </template>
    </permission-alert-gate>
  </div>
</template>

<script>
import Vue from "vue";
import VueNotifications from "vue-notifications";
import BasicHeading from "@/components/shared/BasicHeading";
import HeaderButtonGroup from "@/components/shared/headerbuttons/HeaderButtonGroup";
import LaboratoryCard from "@/components/lab/laboratory/LaboratoryCard";
import LaboratoryContactCard from "@/components/lab/laboratory/LaboratoryContactCard";
import LaboratoryDetailCardstack from "@/components/lab/laboratory/LaboratoryDetailCardstack";
import PermissionAlertGate from "@/components/permission/PermissionAlertGate";
import { AccessControlCheck } from "@/auth/AccessControlCheck";
import { getModel } from "@/shared/fetch-utils";
import { LabRepositoryFactory } from "@/repositories/lab/LabRepositoryFactory";
import { notificationOptions } from "@/shared/toast-notification-options";
import { laboratoryNotifications } from "@/shared/notifications";

const LaboratoryRepository = LabRepositoryFactory.get("laboratory");

Vue.use(VueNotifications, notificationOptions);

export default {
  name: "LaboratoryDetail",
  components: {
    BasicHeading,
    HeaderButtonGroup,
    LaboratoryCard,
    LaboratoryContactCard,
    LaboratoryDetailCardstack,
    PermissionAlertGate,
  },
  data() {
    return {
      loading: true,
      laboratory_id: this.$route.params.laboratory_id || null,
      laboratory: null,
    };
  },
  computed: {
    button_config() {
      return {
        buttons: [
          {
            icon: "fa-question-circle",
            linkto: `/laboratory/${this.laboratory_id}/service-questions`,
            title: "Laboratory service questions",
            variant: "info",
          },
          {
            icon: "fa-list",
            linkto: "/laboratory",
            title: "Laboratory list",
          },
          {
            icon: "fa-pencil",
            linkto: `/laboratory/${this.laboratory_id}/edit`,
            title: "Edit laboratory",
            variant: "warning",
            permission: this.checkPermission("edit laboratory"),
          },
        ],
      };
    },
  },
  methods: {
    checkPermission(permission) {
      return AccessControlCheck.checkPermission(permission);
    },
    showDeleteModal() {
      this.$bvModal
        .msgBoxConfirm(
          "You are about to remove this laboratory. Do you want to continue?",
          {
            title: "Are you sure?",
            modalClass: ["modal-danger"],
            size: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            centered: true,
          }
        )
        .then(async (confirm) => {
          if (confirm) {
            try {
              await LaboratoryRepository.suspend(
                this.laboratory_id
              );

              this.deleteSuccess();
                this.$router.push("/laboratory");
            } catch ({ response }) {
              const { status, data } = response.response;
  
              this.$bvToast.toast(`Code: ${status}. ${data.error}`, {
                title: "Server Error.",
                variant: "danger",
              });
            }
          }
        });
    },
  },
  async created() {
    this.loading = true;

    this.laboratory = await getModel({
      model_type: "laboratory",
      id: this.laboratory_id,
      message: this.$bvToast,
    });

    this.loading = false;
  },
  notifications: laboratoryNotifications,
};
</script>
